/**
 * packages/auth/src/services/appUserService.ts
 * 
 * Service for managing app-specific user profiles.
 * Allows the same Supabase auth user to have different profiles per app
 * (e.g., brand profile in koordinator-app, customer profile in katalog-app).
 */

import { supabase } from '@bright-web-skeleton/supabase';

export type AppId = 'koordinator' | 'katalog' | 'kloutforce' | 'admin';

export interface AppUserMapping {
  id: string;
  auth_user_id: string;
  app_name: string;
  app_user_id: string;
  created_at: string | null;
}

/**
 * Get the app-specific user ID for a given auth user and app
 */
export async function getAppUserIdForAuthUser(
  authUserId: string, 
  appId: AppId
): Promise<string | null> {
  try {
    // Koordinator: resolve brand id directly from brand_users / brands.user_id.
    // Do NOT use app_user_map for koordinator — historical rows may point to
    // members.id (a profile id), which would then be mis-queried as brands.id
    // and produce 406 errors against the brands table.
    if (appId === 'koordinator') {
      const { data: bu } = await supabase
        .from('brand_users')
        .select('brand_id')
        .eq('user_id', authUserId)
        .in('role', ['owner', 'admin'])
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();
      if (bu?.brand_id) return bu.brand_id as string;

      const { data: legacy } = await supabase
        .from('brands')
        .select('id')
        .eq('user_id', authUserId)
        .limit(1)
        .maybeSingle();
      return (legacy?.id as string) ?? null;
    }

    // Kloutforce: resolve member id directly from members.user_id (canonical
    // post-dedupe). Legacy app_user_map rows may point to deleted member ids
    // (Phase 1 dedupe removed loser rows), which would leave the profile
    // page stuck in a loading state. The members table now has a unique
    // index on user_id, so this is deterministic.
    if (appId === 'kloutforce') {
      const { data: member, error: memberErr } = await supabase
        .from('members')
        .select('id')
        .eq('user_id', authUserId)
        .order('onboarding_completed_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (memberErr) {
        console.warn('[appUserService] kloutforce members lookup failed:', memberErr.message);
        return null;
      }
      return (member?.id as string) ?? null;
    }

    // Use maybeSingle() instead of single() to avoid 406 error
    // when no rows exist (e.g., Koordinator users don't have app_user_map entries)
    const { data, error } = await supabase
      .from('app_user_map')
      .select('app_user_id')
      .eq('auth_user_id', authUserId)
      .eq('app_name', appId)
      .limit(1)
      .maybeSingle();

    if (error) {
      // Log warning but don't throw - this is expected for some apps like Koordinator
      console.warn(`[appUserService] app_user_map lookup failed for ${appId}:`, error.message);
      return null;
    }
    
    return data?.app_user_id ?? null;
  } catch (error) {
    console.error('Error fetching app user mapping:', error);
    return null;
  }
}

/**
 * Get the app-specific profile for a given app and user ID
 */
export async function getAppProfile(
  appId: AppId, 
  appUserId: string
): Promise<Record<string, unknown> | null> {
  try {
    // Each app has its own profile table
    const tableMap: Record<AppId, string> = {
      koordinator: 'brands',
      katalog: 'customers',
      kloutforce: 'members',
      admin: 'admins',
    };

    const table = tableMap[appId];
    if (!table) return null;

    // For admin app, look up by user_id since that's how admins table is structured
    if (appId === 'admin') {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('user_id', appUserId)
        .maybeSingle();

      if (error) return null;
      return data;
    }

    // For other apps, look up by id (the app-specific user id)
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('id', appUserId)
      .maybeSingle();

    if (error) return null;
    return data;
  } catch (error) {
    console.error('Error fetching app profile:', error);
    return null;
  }
}

/**
 * Link an auth user to an app-specific profile
 */
export async function linkAuthUserToApp(
  authUserId: string,
  appId: AppId,
  appUserId: string
): Promise<void> {
  const { error } = await supabase
    .from('app_user_map')
    .insert({
      auth_user_id: authUserId,
      app_name: appId,
      app_user_id: appUserId,
    });

  if (error) {
    // If it's a unique constraint violation, update instead
    if (error.code === '23505') {
      await supabase
        .from('app_user_map')
        .update({ app_user_id: appUserId })
        .eq('auth_user_id', authUserId)
        .eq('app_name', appId);
    } else {
      throw error;
    }
  }
}

/**
 * Get all app profiles for a given auth user
 */
export async function getAllAppProfilesForUser(
  authUserId: string
): Promise<AppUserMapping[]> {
  try {
    const { data, error } = await supabase
      .from('app_user_map')
      .select('*')
      .eq('auth_user_id', authUserId);

    if (error) return [];
    return data || [];
  } catch (error) {
    console.error('Error fetching all app profiles:', error);
    return [];
  }
}

/**
 * Check if a user has a profile for a specific app
 */
export async function hasAppProfile(
  authUserId: string, 
  appId: AppId
): Promise<boolean> {
  const appUserId = await getAppUserIdForAuthUser(authUserId, appId);
  if (!appUserId) return false;
  
  const profile = await getAppProfile(appId, appUserId);
  return profile !== null;
}
